import type { ComponentType } from "react";
import { Navigate, type RouteObject, createBrowserRouter } from "react-router";

import { RouteErrorFallback } from "@/components/ErrorBoundary";
import { AppShell } from "@/components/app-shell";
import { configuredDocsOrigin } from "@/lib/docs-origin";
import AccountLayout from "@/routes/account/layout";
import ChatPage from "@/routes/chat";
import GraphLayout from "@/routes/graph/layout";
import LoginPage from "@/routes/login";
import NotFoundPage from "@/routes/not-found";
import { SettingsIndexRedirect } from "@/routes/settings/index-redirect";
import SettingsLayout, { AdminSettingsLayout } from "@/routes/settings/layout";

// Code-split: none of these are needed for first paint (landing chat) or
// the login screen, and several pull in heavy vendors (recharts, the
// validator, the graph workspace). Same pattern as the pre-existing
// `agents` route below.
//
// A deploy rotates asset hashes, so a tab left open across one fails the
// dynamic import of a chunk the server no longer has. The single reload that
// recovers from it lives in `lib/chunk-load-error.ts` (one guard shared with
// `React.lazy` components); when it cannot help, the import rejects with its
// original error and the route's error element explains it.
export function lazyRoute(loader: () => Promise<{ default: ComponentType }>) {
  return async () => ({ Component: (await loader()).default });
}

// Shown only during a hard navigation (full page load) that lands directly
// on a `route.lazy` route, while its chunk is still being fetched (and, for
// the top-level wrapper, while its own loader/lazy resolves before any
// child can render). Kept tiny and dependency-free (no icon library, no app
// components) since it ships in the entry chunk itself.
const hydrateFallbackElement = (
  <div className="flex h-screen w-screen items-center justify-center">
    <div className="size-8 animate-pulse rounded-full bg-muted-foreground/40" />
  </div>
);

const baseRoutes: RouteObject[] = [
  { path: "/login", element: <LoginPage /> },
  { path: "/forgot-password", lazy: lazyRoute(() => import("@/routes/forgot-password")) },
  { path: "/reset-password", lazy: lazyRoute(() => import("@/routes/reset-password")) },
  // Session-gated but outside AppShell: it must preserve `authorization_id`
  // across the login round trip (its own guard, not AppShell's).
  { path: "/oauth/consent", lazy: lazyRoute(() => import("@/routes/oauth-consent")) },
  { path: "/shared/report", lazy: lazyRoute(() => import("@/routes/shared-report")) },
  { path: "/shared/turn", lazy: lazyRoute(() => import("@/routes/shared-turn")) },
  {
    element: <AppShell />,
    children: [
      { path: "/api-reference", lazy: lazyRoute(() => import("@/routes/api-reference")) },
      {
        path: "/api-reference/services/:serviceId/operations/:operationId",
        lazy: lazyRoute(() => import("@/routes/api-reference")),
      },
      // add-docs-changelog design §8: the administrators' inbox, a chunk of its own
      // (admin-only inside, through `RequireRole`).
      {
        path: "/api-reference/changelog",
        lazy: lazyRoute(() => import("@/routes/api-reference-changelog")),
      },
      // add-api-evidence-dossier design, "Admin UI": a service's evidence dossier, a chunk of its
      // own (admin-only inside, through `RequireRole`).
      {
        path: "/api-reference/services/:serviceId/evidence",
        lazy: lazyRoute(() => import("@/routes/api-reference-evidence")),
      },
      {
        path: "/shared/team/reports/:reportId/revisions/:revisionNumber",
        lazy: lazyRoute(() => import("@/routes/shared-team-report")),
      },
      {
        path: "/shared/team/turns/:grantId",
        lazy: lazyRoute(() => import("@/routes/shared-team-turn")),
      },
      { path: "/", element: <ChatPage /> },
      // `:sessionId?` makes the session id an optional path segment: bare
      // `/chat` and `/chat/<id>` share the same element. The same ChatPage
      // component reads `?refine-knowledge=<id>` from the URL regardless of
      // the path prefix (deep-link from the `/settings/sources` chat-row
      // Refinar action).
      { path: "/chat/:sessionId?", element: <ChatPage /> },
      // Legacy URLs (chat history, bookmarks): the pages moved under settings.
      // `:id?` covers both `/story-preparations` and `/story-preparations/<id>`.
      {
        path: "/story-preparations/:id?",
        lazy: lazyRoute(() => import("@/routes/story-preparations/redirect")),
      },
      { path: "/upload", element: <Navigate to="/settings/sources" replace /> },
      { path: "/sources", element: <Navigate to="/settings/sources" replace /> },
      { path: "/validator", element: <Navigate to="/settings/knowledge" replace /> },
      {
        path: "/graph",
        // Kept static: GraphLayout measured 313 bytes, not worth its own
        // chunk. Its child (`settings/graph`, the react-force-graph
        // workspace) stays lazy.
        element: <GraphLayout />,
        children: [{ index: true, lazy: lazyRoute(() => import("@/routes/settings/graph")) }],
      },
      {
        path: "/account",
        // Kept static: AccountLayout measured 336 bytes, not worth its own
        // chunk. Its children stay lazy.
        element: <AccountLayout />,
        children: [
          { index: true, element: <Navigate to="memory" replace /> },
          { path: "memory", lazy: lazyRoute(() => import("@/routes/settings/user-memory")) },
          { path: "api-tokens", lazy: lazyRoute(() => import("@/routes/settings/api-tokens")) },
          { path: "appearance", lazy: lazyRoute(() => import("@/routes/settings/appearance")) },
          { path: "activity", lazy: lazyRoute(() => import("@/routes/settings/account-activity")) },
        ],
      },
      {
        path: "/settings",
        element: <SettingsLayout />,
        children: [
          { index: true, element: <SettingsIndexRedirect /> },
          { path: "sources", lazy: lazyRoute(() => import("@/routes/sources")) },
          { path: "knowledge", lazy: lazyRoute(() => import("@/routes/validator")) },
          // Contributor-visible like `sources`: outside the admin-only subtree.
          {
            path: "story-preparations",
            lazy: lazyRoute(() => import("@/routes/story-preparations/index")),
          },
          {
            path: "story-preparations/:id",
            lazy: lazyRoute(() => import("@/routes/story-preparations/detail")),
          },
          { path: "graph", element: <Navigate to="/graph" replace /> },
          { path: "user-memory", element: <Navigate to="/account/memory" replace /> },
          { path: "api-tokens", element: <Navigate to="/account/api-tokens" replace /> },
          { path: "appearance", element: <Navigate to="/account/appearance" replace /> },
          {
            element: <AdminSettingsLayout />,
            children: [
              { path: "models", lazy: lazyRoute(() => import("@/routes/settings/models")) },
              {
                path: "models/chat",
                lazy: lazyRoute(() => import("@/routes/settings/models-chat")),
              },
              {
                path: "models/agents",
                lazy: lazyRoute(() => import("@/routes/settings/models-agents")),
              },
              {
                path: "models/judges",
                lazy: lazyRoute(() => import("@/routes/settings/models-judges")),
              },
              {
                path: "rate-limits",
                lazy: lazyRoute(() => import("@/routes/settings/rate-limits")),
              },
              { path: "system", lazy: lazyRoute(() => import("@/routes/settings/system")) },
              {
                path: "system/retrieval-evaluations/:runId",
                lazy: lazyRoute(() => import("@/routes/settings/retrieval-evaluation-detail")),
              },
              { path: "users", lazy: lazyRoute(() => import("@/routes/settings/users")) },
              {
                path: "users/:userId",
                lazy: lazyRoute(() => import("@/routes/settings/user-detail")),
              },
              { path: "dlq", lazy: lazyRoute(() => import("@/routes/settings/dlq")) },
              {
                path: "agents",
                // Code-split: the specialized-agents workspace is admin-only
                // and heavy enough to push the entry chunk past the 2 MiB
                // workbox precache limit if bundled statically.
                lazy: lazyRoute(() => import("@/routes/settings/agents")),
              },
              {
                path: "web-test-targets",
                lazy: lazyRoute(() => import("@/routes/settings/web-test-targets")),
              },
              {
                path: "system-tasks",
                lazy: lazyRoute(() => import("@/routes/settings/system-tasks")),
              },
              {
                path: "automation",
                lazy: lazyRoute(() => import("@/routes/settings/automation")),
              },
              {
                path: "integrations",
                lazy: lazyRoute(() => import("@/routes/settings/integrations")),
              },
              { path: "usage", lazy: lazyRoute(() => import("@/routes/settings/usage")) },
              {
                path: "live-tools",
                lazy: lazyRoute(() => import("@/routes/settings/live-tools")),
              },
              { path: "health", lazy: lazyRoute(() => import("@/routes/settings/health")) },
              {
                path: "knowledge-repositories",
                lazy: lazyRoute(() => import("@/routes/settings/knowledge-repositories")),
              },
              {
                path: "knowledge-quality",
                lazy: lazyRoute(() => import("@/routes/settings/knowledge-quality")),
              },
              { path: "audit", lazy: lazyRoute(() => import("@/routes/settings/audit")) },
              {
                path: "feedback",
                lazy: lazyRoute(() => import("@/routes/settings/feedback/index")),
              },
              {
                path: "feedback/:id",
                lazy: lazyRoute(() => import("@/routes/settings/feedback/detail")),
              },
              {
                path: "benchmarks",
                lazy: lazyRoute(() => import("@/routes/settings/benchmarks")),
              },
              {
                path: "benchmarks/:runId",
                lazy: lazyRoute(() => import("@/routes/settings/benchmarks-detail")),
              },
            ],
          },
        ],
      },
      // Catch-all: any URL matching no route above. It lives inside AppShell so
      // signed-in users keep the nav chrome and can navigate away; anonymous
      // visitors hit AppShell's own gate and land on /login, same as for every
      // other gated path. react-router ranks splats last, so array position
      // doesn't change what matches — it's kept here so reading order matches
      // match order.
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export const appRoutes = baseRoutes.map(withRouteErrorElement);

// One loader for the changelog's three routes (see `publicDocsRoutes`).
const changelogRoute = lazyRoute(() => import("@/routes/public-changelog"));

// make-published-docs-public spec, "The docs host serves the public portal
// at `/`" (design.md, A4): `/` is already claimed by `ChatPage` inside the
// authenticated app tree, and the anonymous public portal cannot simply be
// added as a second route at the same path — React Router cannot register
// two elements at one path, and `AppShell`'s own auth gate would in any case
// redirect an anonymous docs-host visitor to `/login` before ever reaching
// it. The portal instead replaces the ENTIRE route table for the identical
// build artifact, conditionally on which host is serving it — the docs
// host's bundle renders only the public portal (or its not-found state) for
// every path it receives, while every other origin (including local
// development, where no docs origin need be configured) keeps the full
// authenticated app route table exactly as before.
export const publicDocsRoutes: RouteObject[] = [
  { path: "/", lazy: lazyRoute(() => import("@/routes/public-docs")) },
  // add-docs-changelog design §7: the changelog is the one section with paths of
  // its own. The three routes share one lazy module, so the entry chunk does not
  // grow; the static `api` segment ranks above `:slug` (a slug starts with a year,
  // so they never collide).
  { path: "/changelog", lazy: changelogRoute },
  { path: "/changelog/api", lazy: changelogRoute },
  { path: "/changelog/:slug", lazy: changelogRoute },
  // Pre-review finding 11: the docs host's catch-all has no `AppShell`
  // ancestor (unlike `appRoutes`'s own) and no signed-in reader to invite
  // back to "continuar trabajando" — `variant="public"` centers the page in
  // its own full-height wrapper and swaps in public-appropriate copy.
  { path: "*", element: <NotFoundPage variant="public" /> },
].map(withRouteErrorElement);

export function createAppRouter() {
  const routes = configuredDocsOrigin() === window.location.origin ? publicDocsRoutes : appRoutes;
  return createBrowserRouter(routes);
}

function withRouteErrorElement(route: RouteObject): RouteObject {
  if (route.index === true) {
    return {
      ...route,
      errorElement: route.errorElement ?? <RouteErrorFallback />,
      hydrateFallbackElement: route.hydrateFallbackElement ?? hydrateFallbackElement,
    };
  }

  if (route.children === undefined) {
    return {
      ...route,
      errorElement: route.errorElement ?? <RouteErrorFallback />,
      hydrateFallbackElement: route.hydrateFallbackElement ?? hydrateFallbackElement,
    };
  }

  return {
    ...route,
    errorElement: route.errorElement ?? <RouteErrorFallback />,
    hydrateFallbackElement: route.hydrateFallbackElement ?? hydrateFallbackElement,
    children: route.children.map(withRouteErrorElement),
  };
}
